import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import input from "../../../../assets/styles/components/input.module.css";
import Button from "../../../../components/Button/Button";
import styles from "./CashFlow.module.css";

import { Add, ArrowBack, ArrowDropDown, ArrowDropUp, Delete, FilterAlt, FilterAltOffSharp, Search, Edit } from "@mui/icons-material";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import TableContainer from "../../../../components/TableContainer/TableContainer";

import {
  deleteMoviments,
  getAccountBalances,
  getMovimentsCashFlow,
  type ICashFlowFilters,
  type ICashFlowOrder,
} from "../../../../services/cashFlow";
import { formatDate } from "../../../../utils/useMask";
import Filter from "./Filter";
import SummaryCards from "./SummaryCards";
import { useIsMobile } from "../../../../utils/useIsMobile";
import { toast } from "react-toastify";
import ModalDelete from "../../../../components/ModalDelete/ModalDelete";
import InputSearch from "../../../../components/InputSearch/InputSearch";

type OrderField = keyof ICashFlowOrder;

const SESSION_SCROLL_KEY = "cashflow_scroll";

const getInitialDates = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).toISOString().split("T")[0];
  const lastDay = new Date(year, month + 1, 0).toISOString().split("T")[0];
  return { firstDay, lastDay };
};

// ─── Helpers de URL params ──────────────────────────────────────────────────
function buildOrderFromParams(params: URLSearchParams): ICashFlowOrder {
  const field = params.get("orderField") as OrderField | null;
  const dir = params.get("orderDir") as "ASC" | "DESC" | null;
  if (field && dir) {
    return { [field]: dir } as ICashFlowOrder;
  }
  return { data: "DESC" };
}

function buildFilterFromParams(
  params: URLSearchParams,
  firstDay: string,
  lastDay: string
): ICashFlowFilters {
  return {
    inicio: params.get("inicio") ?? firstDay,
    fim: params.get("fim") ?? lastDay,
    conta_financeira: params.get("conta_financeira") ?? "",
    uid_plano_contas: params.get("uid_plano_contas") ?? "",
    uid_centro_custo: params.get("uid_centro_custo") ?? "",
    tipo: params.get("tipo") as "entrada" | "saida" | "" ?? "",
    tipo_data: params.get("tipo_data") as "movimentacao" | "competencia" | "vencimento" ?? "movimentacao",
    realizado: params.get("realizado") !== "false", 
    previsto: params.get("previsto") !== "false", 
    incluir_atrasados: params.get("incluir_atrasados") === "true",
  };
}

const CashFlow = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { firstDay, lastDay } = getInitialDates();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; descricao: string } | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (uid: string) => deleteMoviments(uid),
    onSuccess: (res) => {
      toast.success(res.message || "Movimentação excluída com sucesso");
      queryClient.invalidateQueries({ queryKey: ["getMovimentsCashFlow"] });
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(err.message || "Erro ao excluir movimentação");
    },
  });

  // ── Estado derivado da URL ──────────────────────────────────────────────────
  const search = searchParams.get("search") ?? "";
  const orderTable = useMemo(() => buildOrderFromParams(searchParams), [searchParams]);
  const dataFilter = useMemo(() => buildFilterFromParams(searchParams, firstDay, lastDay), [searchParams, firstDay, lastDay]);

  // ── Estado local de UI ─────────────────────────────────────────────────────
  const [isVisible, setIsVisible] = useState(false);
  const [tempSearch, setTempSearch] = useState(search);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const [scrollRestored, setScrollRestored] = useState(false);

  // ─── Atualização de URL ────────────────────────────────────────────────────
  const updateParams = (updates: Record<string, string | boolean | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  };

  // Mock para SummaryCards funcionar com URL
  const handleSetDataFilter = (updater: any) => {
    const next = typeof updater === "function" ? updater(dataFilter) : updater;
    updateParams(next);
  };

  // ─── Query: Carrega tudo (sem paginação) ────────────────────────────────────
  const { data: fetchResult, isLoading } = useQuery({
    queryKey: ["getMovimentsCashFlow", dataFilter, search],
    queryFn: () => getMovimentsCashFlow(dataFilter, search),
  });

  const moviments = fetchResult?.movimentacoes || [];
  const saldoInicial = fetchResult?.saldoInicial || 0;

  const { data: contas = [] } = useQuery({
    queryKey: ["getAccountBalances"],
    queryFn: () => getAccountBalances(),
  });

  // ─── Ordenação no Frontend ─────────────────────────────────────────────────
  const sortedMoviments = useMemo(() => {
    if (!moviments.length) return [];

    // Clona o array original para não mutar o cache do React Query
    const dadosParaOrdenar = [...moviments];
    const [key, direction] = Object.entries(orderTable)[0] as [OrderField, "ASC" | "DESC"];

    const orderMap: Record<OrderField, string> = {
      codigo: "codigo_estabelecimento",
      data: "data",
      fluxo: "fluxo",
      tipo: "tipo",
      descricao: "descricao",
      plano: "plano_contas",
      centro: "centro_custo",
      conta: "conta_financeira",
      valor: "valor",
    };

    const field = orderMap[key];

    if (field) {
      dadosParaOrdenar.sort((a, b) => {
        let valA = a[field];
        let valB = b[field];

        if (valA === null || valA === undefined) valA = "";
        if (valB === null || valB === undefined) valB = "";

        if (key === "codigo") {
          const isAEmpty = valA === "-" || valA === "";
          const isBEmpty = valB === "-" || valB === "";

          if (isAEmpty && isBEmpty) return 0;
          if (isAEmpty) return direction === "ASC" ? -1 : 1;
          if (isBEmpty) return direction === "ASC" ? 1 : -1;

          valA = Number(valA) || 0;
          valB = Number(valB) || 0;
        }
        else if (key === "data") {
          valA = new Date(valA).getTime() || 0;
          valB = new Date(valB).getTime() || 0;
        } 
        else if (key === "valor") {
          valA = Number(valA) || 0;
          valB = Number(valB) || 0;
        } 
        else {
          valA = String(valA).toLowerCase();
          valB = String(valB).toLowerCase();
        }

        if (valA < valB) return direction === "ASC" ? -1 : 1;
        if (valA > valB) return direction === "ASC" ? 1 : -1;
        return 0;
      });
    }

    return dadosParaOrdenar;
  }, [moviments, orderTable]);

  // ─── Cálculos dos Cards ────────────────────────────────────────────────────
  const { entradas, saidas, resultado, atrasado, saldoConsolidado, saldoFinal } = useMemo(() => {
    let ent = 0, sai = 0, atr = 0;
    moviments.forEach((m: any) => {
      const v = Number(m.valor) || 0;
      if (m.fluxo === "Atrasado") atr += v;
      if (m.tipo === "entrada") ent += v;
      if (m.tipo === "saida") sai += v;
    });
    const cons = contas.reduce((acc: number, c: any) => acc + Number(c.saldo), 0);
    return { entradas: ent, saidas: sai, resultado: ent - sai, atrasado: atr, saldoConsolidado: cons, saldoFinal: saldoInicial + ent - sai };
  }, [moviments, contas, saldoInicial]);

  // ─── Persistência de Scroll ────────────────────────────────────────────────
  useEffect(() => {
    if (scrollRestored || isLoading || moviments.length === 0) return;
    const el = tbodyRef.current;
    if (!el) return;

    const savedScroll = sessionStorage.getItem(SESSION_SCROLL_KEY);
    if (savedScroll) {
      requestAnimationFrame(() => {
        el.scrollTop = parseInt(savedScroll, 10);
        setScrollRestored(true);
      });
    } else {
      setScrollRestored(true);
    }
  }, [moviments.length, isLoading, scrollRestored]);

  useEffect(() => {
    const el = tbodyRef.current;
    if (!el) return;
    const handleScroll = () => {
      sessionStorage.setItem(SESSION_SCROLL_KEY, String(el.scrollTop));
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = setTimeout(() => updateParams({ search: tempSearch || undefined }), 500);
    return () => clearTimeout(handler);
  }, [tempSearch]);

  const handleResetFilter = () => {
    setTempSearch("");
    const nextParams = new URLSearchParams();
    nextParams.set("inicio", firstDay);
    nextParams.set("fim", lastDay);
    
    setSearchParams(nextParams, { replace: true });
  };

  const handleChangeOrder = (field: OrderField) => {
    const currentField = searchParams.get("orderField");
    const currentDir = searchParams.get("orderDir");
    const newDir = currentField === field && currentDir === "ASC" ? "DESC" : "ASC";
    updateParams({ orderField: field, orderDir: newDir });
  };

  const renderSortableHeader = (field: OrderField, label: string) => {
    const isActive = searchParams.get("orderField") === field;
    const dir = isActive ? searchParams.get("orderDir") : undefined;
    return (
      <Button variant="textButton" onClick={() => handleChangeOrder(field)}>
        {label}
        {isActive ? (dir === "DESC" ? <ArrowDropDown /> : <ArrowDropUp />) : ""}
      </Button>
    );
  };

  const openLink = (e: React.MouseEvent, m: any) => {
    console.log(e);
    
    const url = m.fluxo === "Realizado" 
      ? `/agro/financialHub/cashFlow/edit/${m.id}` 
      : `/agro/financialHub/${m.tipo === "entrada"
        ? "accountsReceivable"
        : "accountsPayable"
      }/edit/${m.uid_financeiro_lancamento}`;
    navigate(url);
  };

  return (
    <div className={styles.containerFlow}>
      <div className={styles.cashFlow}>
        <div className={styles.header}>
          <h1>Fluxo de Caixa</h1>

          <InputSearch
            searchValue={tempSearch}
            setSearchValue={setTempSearch}
            placeholder="Pesquisar"
          />

          <div className={styles.headerButtons}>
            <p>Qtd:<span>{moviments.length}</span></p>
            <Button variant="contain" onClick={handleResetFilter}><FilterAltOffSharp /></Button>
            <Button variant="contain" onClick={() => setIsVisible(true)}><FilterAlt /></Button>
            <Button variant="contain" onClick={() => navigate("add")}><Add /></Button>
            <Button variant="contain" onClick={() => navigate(-1)}><ArrowBack /></Button>
          </div>
          {isVisible && (
            <Filter
              dataFilter={dataFilter}
              setDataFilter={handleSetDataFilter}
              onClose={() => setIsVisible(false)}
            />
          )}
        </div>

        <SummaryCards
          saldoInicial={saldoInicial}
          entradas={entradas}
          saidas={saidas}
          resultado={resultado}
          atrasado={atrasado}
          saldoFinal={saldoFinal}
          saldoConsolidado={saldoConsolidado}
          contas={contas}
          dataFilter={dataFilter}
          setDataFilter={handleSetDataFilter}
        />

        <div className={styles.containerTable}>
          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 4px 1rem" }}>
              {sortedMoviments.length === 0 && (
                <p style={{ textAlign: "center", color: "#999", padding: "2rem 0" }}>Nenhum registro encontrado.</p>
              )}
              {sortedMoviments.map((m: any) => (
                <div key={m.id}
                  style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", border: "1px solid #eee", cursor: "pointer" }}
                  onMouseDown={(e) => { if (!(e.target as HTMLElement).closest("button")) openLink(e, m); }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <strong style={{ fontSize: "0.95rem", color: "#222", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.descricao}
                    </strong>
                    <span style={{
                      padding: "3px 10px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700, marginLeft: 8,
                      background: m.tipo === "entrada" ? "#d4edda" : m.tipo === "saida" ? "#f8d7da" : "#e2e3e5",
                      color: m.tipo === "entrada" ? "#155724" : m.tipo === "saida" ? "#b65f68" : "#495057",
                    }}>
                      {m.tipo === "entrada" ? "Entrada" : m.tipo === "saida" ? "Saída" : "Transf."}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", margin: "8px 0 12px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Valor</span>
                      <strong style={{ fontSize: "1.05rem" }}>{Number(m.valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Data</span>
                      <span>{formatDate(m.data, true)}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Fluxo</span>
                      <span>{m.fluxo}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f0f0f0", paddingTop: 10 }}
                    onClick={(e) => e.stopPropagation()}>
                    <button style={{ width: 34, height: 34, borderRadius: 8, border: "none", background: "var(--light-blue-dark)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      onMouseDown={(e) => openLink(e, m)}>
                      <Edit fontSize="small" />
                    </button>
                    <button style={{ width: 34, height: 34, borderRadius: 8, border: "none", background: "var(--red-primary, #e74c3c)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      onClick={() => setDeleteTarget({ id: m.id, descricao: m.descricao })}>
                      <Delete fontSize="small" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <TableContainer
            tbodyRef={tbodyRef}
            columns={[
              { key: "codigoEx", titulo: renderSortableHeader("codigo", "Cód."), classStyle: styles.columnCode },
              { key: "dataEx", titulo: renderSortableHeader("data", "Data"), classStyle: styles.columnDate },
              { key: "fluxoEx", titulo: renderSortableHeader("fluxo", "Fluxo"), classStyle: styles.columnOrigin },
              { key: "tipoEx", titulo: renderSortableHeader("tipo", "Tipo"), classStyle: styles.columnType },
              { key: "descricaoEx", titulo: renderSortableHeader("descricao", "Descrição"), classStyle: styles.columnDesc },
              { key: "planoEx", titulo: renderSortableHeader("plano", "Plano de contas"), classStyle: styles.columnPlan },
              { key: "centroEx", titulo: renderSortableHeader("centro", "Centro de Custo"), classStyle: styles.columnCenter },
              { key: "contaEx", titulo: renderSortableHeader("conta", "conta Fin."), classStyle: styles.columnAccount },
              { key: "valorEx", titulo: renderSortableHeader("valor", "Valor"), classStyle: styles.columnValue },
              { key: "actionEx", titulo: "Ação", classStyle: styles.columnAction },
            ]}
            content={sortedMoviments.map((m: any) => ({
              values: {
                codigoEx: m.codigo_estabelecimento,
                dataEx: formatDate(m.data, true),
                fluxoEx: m.fluxo,
                tipoEx: m.tipo.charAt(0).toUpperCase() + m.tipo.slice(1),
                descricaoEx: m.descricao,
                planoEx: m.plano_contas,
                centroEx: m.centro_custo,
                contaEx: m.conta_financeira,
                valorEx: Number(m.valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
              },
              buttons: [
                { variant: "contain", typeStyle: "small", onMouseDown: (e: any) => openLink(e, m), icon: <Edit /> },
                { variant: "contain", typeStyle: ["small", "cancel"], onClick: () => setDeleteTarget({ id: m.id, descricao: m.descricao }), icon: <Delete /> },
              ],
              onMouseDown: (e: any) => { if (!e.target.closest("button")) openLink(e, m); },
            }))}
          />
          )}
        </div>
      </div>

      {/* Novo Modal de Deleção */}
      <ModalDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && mutate(deleteTarget.id)}
        isDeleting={isPending}
        entity="movimentação"
        title={deleteTarget?.descricao || ""}
        isMobile={isMobile}
      />
    </div>
  );
};

export default CashFlow;